import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full overflow-hidden rounded-xl bg-primary-900">
      <div className="relative h-[440px] w-full md:h-[520px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.hero}
              alt={`${item.title} 剧照`}
              title={`${item.title} - 555电影网`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full max-w-2xl flex-col justify-end px-6 pb-12 md:px-12 md:pb-16">
          <span className="mb-3 flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
            <i className="ri-fire-fill"></i>
            {active.type === "movie" ? "最新电影" : "热播剧集"}
          </span>
          <h1 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-2 text-sm text-background-200 md:text-base">{active.tagline}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-100">
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
            <span>{active.year}</span>
            <span>{active.region}</span>
            <span>{active.genres.slice(0, 3).join(" / ")}</span>
          </div>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200">
            {active.synopsis}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={active.type === "movie" ? "/category/movie" : "/category/tv"}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 bg-primary-950/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-950/60"
            >
              更多同类
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          className="absolute left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          className="absolute right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 right-6 z-10 flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到 ${item.title}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-accent-500" : "w-2 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}